import moment from 'moment';
import React from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import BuildingsAsyncDropDown from '../../../components/BuildingsAsyncDropDown';
import UserAsyncDropDown from '../../../components/UserAsyncDropDown';
import { CustomDatePicker } from '../../../components/CustomSearchBar';
import { Select, SelectItem } from '../../../components/ui/Select';
import { ErrorText } from '../../../components/utils/AvatarCropper';
import { Fonts, Palette } from '../../../components/utils';
import { AMENITY_PARKING } from '../../../helper/types';
import locale from 'antd/es/date-picker/locale/fr_FR';
import localeEn from 'antd/es/date-picker/locale/en_US';
import i18next from 'i18next';
import { MdError } from 'react-icons/md';
import { InputSeparator, SettingsModalInput } from '../../settings/SettingsModals';
import { FieldLabel } from '../../../components/WizardV2/wizardStylesV2';

const ResidentPaymentErrorNotice = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${Palette.BACKGROUND_TAG_RED};
  border: 1px solid ${Palette.ERROR}33;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ResidentPaymentErrorText = styled.span`
  flex: 1;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 20px;
  color: ${Palette.ERROR};
`;

const NO_TIME = -1;

function isUnsetStartTime(v) {
  return v == null || v === '' || v === NO_TIME || v === String(NO_TIME);
}

function AddBookingDetailStepV2({
  t,
  control,
  errors,
  watch,
  resetField,
  setValue,
  clearErrors,
  amenity,
  disabledDates,
  disabledEndDate,
  getFilteredAmenities,
  selectedAmenityId,
  availabilitySlots,
  endTimeOptions,
  watchStartTime,
  watchStartDate,
  watchAllFields,
  slotsLoading,
  allowMultiDayBooking,
  amenityDisplayName,
  mode = 'add',
  paymentFieldsDisabled = false,
  showResidentPaymentError = false
}) {
  const isEditMode = mode === 'edit';
  const lockBuildingAndResident = isEditMode;
  const bookingFieldsLocked = paymentFieldsDisabled;
  const canPickStartDate = !!watch('user') && !bookingFieldsLocked;
  const amenitySelectDisabled = !isEditMode && !watch('building');
  const startDateLabel = allowMultiDayBooking
    ? t('booking_check_in', { defaultValue: 'Check-in' })
    : t('Select Date');
  const startTimeLabel = allowMultiDayBooking
    ? t('booking_check_in_time', { defaultValue: 'Check-in Time' })
    : t('Select start time');
  const endDateLabel = t('booking_check_out', { defaultValue: 'Check-out' });
  const endTimeLabel = allowMultiDayBooking
    ? t('booking_check_out_time', { defaultValue: 'Check-out Time' })
    : t('Select end time');

  const hasValidAmenity = watch('amenity_id') && parseInt(watch('amenity_id'), 10) > 0;
  const hasValidStartDate = !!watchStartDate;
  const hasValidStartTime = watchStartTime && watchStartTime !== NO_TIME;
  const startTimeDisabled = !hasValidStartDate || bookingFieldsLocked;
  const endTimeDisabled =
    !hasValidStartTime || (allowMultiDayBooking && !watch('endDate')) || bookingFieldsLocked;
  const endDateDisabled = !hasValidStartTime || bookingFieldsLocked;
  
  // Loading states
  const startTimeLoading = slotsLoading && hasValidStartDate && hasValidAmenity;
  const endTimeLoading = slotsLoading && hasValidStartTime && !endTimeDisabled;
  const showNoEndTimesOnDate =
    !slotsLoading &&
    !endTimeDisabled &&
    hasValidStartTime &&
    endTimeOptions.length === 0;

  return (
    <>
      {!isEditMode ? (
        <InputSeparator>
          <FieldLabel>{t('Select Building', { defaultValue: 'Select Building' })}</FieldLabel>
          <Controller
            control={control}
            name="building"
            render={({ field: { onChange, value } }) => (
              <BuildingsAsyncDropDown
                value={value}
                onChange={(e) => {
                  resetField('user');
                  onChange(e);
                }}
                valid={!errors.building}
                invalid={errors.building}
              />
            )}
          />
          {errors.building && <ErrorText>{errors.building.message}</ErrorText>}
        </InputSeparator>
      ) : null}

      <InputSeparator>
        <FieldLabel>{t('Select Amenity', { defaultValue: 'Select Amenity' })}</FieldLabel>
        <Controller
          control={control}
          name="amenity_id"
          render={({ field: { onChange, onBlur, value } }) => {
            const editAmenityOptions =
              isEditMode && value != null && value !== ''
                ? [
                    {
                      value,
                      label:
                        amenityDisplayName ||
                        amenity?.description ||
                        getFilteredAmenities?.()?.find((a) => String(a.id) === String(value))
                          ?.description ||
                        t('Amenity')
                    }
                  ]
                : null;

            const amenityOptions = getFilteredAmenities?.() || [];
            const hasAmenities = amenityOptions.length > 0;

            return (
              <Select
                id="amenity_id"
                onChange={onChange}
                disabled={isEditMode || amenitySelectDisabled}
                placeholder={
                  isEditMode ? t('Locked in edit mode') : 'Please Select'
                }
                custom
                onBlur={onBlur}
                invalid={!!errors.amenity_id}
                value={value}
                options={
                  editAmenityOptions ??
                  (hasAmenities
                    ? [
                        {
                          value: -1,
                          label: t('Please select')
                        },
                        ...amenityOptions.map((a) => ({
                          value: a.id,
                          label: a.description
                        }))
                      ]
                    : [
                        {
                          value: null,
                          label: t('No Amenities Available')
                        }
                      ])
                }
              />
            );
          }}
        />
        {errors.amenity_id && <ErrorText>{errors.amenity_id.message}</ErrorText>}
      </InputSeparator>

      <InputSeparator>
        <FieldLabel>{t('Resident Name')}</FieldLabel>
        <Controller
          control={control}
          name="user"
          render={({ field: { onChange, value } }) => (
            <UserAsyncDropDown
              disabled={lockBuildingAndResident || !hasValidAmenity}
              value={value}
              type={1}
              buildingId={watchAllFields['building']?.value}
              onChange={onChange}
              valid={!errors.user}
              invalid={!!errors.user}
              placeholder={
                lockBuildingAndResident ? t('Locked in edit mode') : t('Select Resident')
              }
              only_residents
            />
          )}
        />
        {errors.user && <ErrorText>{errors.user.message}</ErrorText>}
      </InputSeparator>

      {showResidentPaymentError ? (
        <ResidentPaymentErrorNotice role="alert">
          <MdError size={24} color={Palette.ERROR} style={{ flexShrink: 0 }} />
          <ResidentPaymentErrorText>
            {t('book_amenity_no_credit_card_error', {
              defaultValue:
                'User does not have a credit card on file for this amenity. Please update their payment options.'
            })}
          </ResidentPaymentErrorText>
        </ResidentPaymentErrorNotice>
      ) : null}

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <InputSeparator>
              <FieldLabel>{startDateLabel}</FieldLabel>
              <Controller
                control={control}
                name="startDate"
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    height={'56px'}
                    locale={i18next.resolvedLanguage === 'fr' ? locale : localeEn}
                    disabled={!canPickStartDate}
                    format="YYYY-MM-DD"
                    onChange={onChange}
                    value={value}
                    placeholder={t('Select Start Date')}
                    disabledDate={amenity && disabledDates}
                  />
                )}
              />
              {errors.startDate && <ErrorText>{errors.startDate.message}</ErrorText>}
            </InputSeparator>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <InputSeparator>
              <FieldLabel>{startTimeLabel}</FieldLabel>
              <Controller
                control={control}
                name="startTime"
                render={({ field: { onChange, onBlur, value } }) => {
                  // Check if current value exists in available slots, if not, add it as an option
                  const currentValueExists = value && value !== NO_TIME && 
                    availabilitySlots?.some(slot => slot.start_time === value);
                  
                  const shouldShowCurrentValue = !currentValueExists && value && value !== NO_TIME && 
                    typeof value === 'string' && moment.parseZone(value).isValid();

                  return (
                    <Select
                      loading={startTimeLoading}
                      onBlur={onBlur}
                      onChange={(next) => {
                        if (setValue) {
                          setValue('endTime', NO_TIME, {
                            shouldValidate: false,
                            shouldDirty: true
                          });
                        }
                        if (clearErrors) {
                          clearErrors(['startTime', 'endTime']);
                        }
                        onChange(next);
                      }}
                      disabled={startTimeDisabled}
                      value={value}
                      placeholder={t('Select Start Time')}
                      custom>
                      <SelectItem value={NO_TIME}>{t('Please select')}</SelectItem>
                      {shouldShowCurrentValue && (
                        <SelectItem value={value}>
                          {moment.parseZone(value).format('h:mm a')}
                        </SelectItem>
                      )}
                      {availabilitySlots &&
                        availabilitySlots.map((slot) => {
                          const amPmTime = moment.parseZone(slot.start_time).format('h:mm a');
                          return (
                            <SelectItem key={slot.start_time} value={slot.start_time}>
                              {amPmTime}
                            </SelectItem>
                          );
                        })}
                    </Select>
                  );
                }}
              />
              {errors.startTime && !startTimeDisabled && (
                <ErrorText>{errors.startTime.message}</ErrorText>
              )}
            </InputSeparator>
          </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {allowMultiDayBooking ? (
              <InputSeparator>
                <FieldLabel>{endDateLabel}</FieldLabel>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { onChange, value } }) => (
                    <CustomDatePicker
                      height={'56px'}
                      locale={i18next.resolvedLanguage === 'fr' ? locale : localeEn}
                      disabled={endDateDisabled}
                      format="YYYY-MM-DD"
                      onChange={onChange}
                      value={value}
                      placeholder={t('Select End Date')}
                      disabledDate={amenity && disabledEndDate}
                    />
                  )}
                />
                {errors.endDate && !endDateDisabled && (
                  <ErrorText>{errors.endDate.message}</ErrorText>
                )}
              </InputSeparator>
            ) : null}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <InputSeparator>
              <FieldLabel>{endTimeLabel}</FieldLabel>

              <Controller
                control={control}
                name="endTime"
                render={({ field: { onChange, onBlur, value } }) => {
                  // Check if current value exists in available options, if not, add it as an option
                  const currentValueExists = value && value !== NO_TIME && 
                    endTimeOptions?.some(option => option === value);
                  
                  const shouldShowCurrentValue = !currentValueExists && value && value !== NO_TIME && 
                    typeof value === 'string' && moment.parseZone(value).isValid();

                  return (
                    <Select
                      loading={endTimeLoading}
                      onBlur={onBlur}
                      onChange={onChange}
                      disabled={endTimeDisabled}
                      value={value}
                      placeholder={t('Select End Time')}
                      custom>
                      <SelectItem value={NO_TIME}>{t('Please select')}</SelectItem>
                      {shouldShowCurrentValue && (
                        <SelectItem value={value}>
                          {moment.parseZone(value).format('h:mm a')}
                        </SelectItem>
                      )}
                      {endTimeOptions.map((iso) => {
                        const amPmTime = moment.parseZone(iso).format('h:mm a');
                        return (
                          <SelectItem key={iso} value={iso}>
                            {amPmTime}
                          </SelectItem>
                        );
                      })}
                    </Select>
                  );
                }}
              />
              {showNoEndTimesOnDate && (
                <ErrorText>
                  {t('no_checkout_times_on_date', {
                    defaultValue: 'No checkout times on this date'
                  })}
                </ErrorText>
              )}
              {errors.endTime && !endTimeDisabled && (
                <ErrorText>{errors.endTime.message}</ErrorText>
              )}
            </InputSeparator>
          </div>
      </div>

      {selectedAmenityId === AMENITY_PARKING && (
        <>
          <InputSeparator>
            <FieldLabel>Make</FieldLabel>
            <Controller
              control={control}
              name="make"
              render={({ field: { onChange, onBlur, value } }) => (
                <SettingsModalInput
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  valid={!errors.make}
                  error={errors.make}
                  id="make"
                  type="text"
                  disabled={bookingFieldsLocked}
                  placeholder={t('Enter car make')}
                />
              )}
            />
            {errors.make && <ErrorText>{errors.make.message}</ErrorText>}
          </InputSeparator>

          <InputSeparator>
            <FieldLabel>{t('Model')}</FieldLabel>
            <Controller
              control={control}
              name="model"
              render={({ field: { onChange, onBlur, value } }) => (
                <SettingsModalInput
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  valid={!errors.model}
                  error={errors.model}
                  id="model"
                  type="text"
                  disabled={bookingFieldsLocked}
                  placeholder={t('Enter car model')}
                />
              )}
            />
            {errors.model && <ErrorText>{errors.model.message}</ErrorText>}
          </InputSeparator>

          <InputSeparator>
            <FieldLabel>{t('Color')}</FieldLabel>
            <Controller
              control={control}
              name="color"
              render={({ field: { onChange, onBlur, value } }) => (
                <SettingsModalInput
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  valid={!errors.color}
                  error={errors.color}
                  id="color"
                  type="text"
                  disabled={bookingFieldsLocked}
                  placeholder={t('Enter car color')}
                />
              )}
            />
            {errors.color && <ErrorText>{errors.color.message}</ErrorText>}
          </InputSeparator>

          <InputSeparator>
            <FieldLabel>{t('License plate number')}</FieldLabel>
            <Controller
              control={control}
              name="plate_number"
              render={({ field: { onChange, onBlur, value } }) => (
                <SettingsModalInput
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  valid={!errors.plate_number}
                  error={errors.plate_number}
                  id="plate_number"
                  type="text"
                  disabled={bookingFieldsLocked}
                  placeholder={t('Enter plate number')}
                />
              )}
            />
            {errors.plate_number && <ErrorText>{errors.plate_number.message}</ErrorText>}
          </InputSeparator>
        </>
      )}
    </>
  );
}

export default AddBookingDetailStepV2;
